import React, { useMemo } from "react";
import { useStoredValue } from "../../../util";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { RawAddressWithIndices, SovGridContext } from "./types";
import {
  buildPivotTable,
  buildCrossTab,
  configFromFields,
  CrossTabResult,
  PivotTableResult,
} from "./pivotUtils";
import {
  GROUPABLE_FIELDS,
  GroupableField,
  isFieldDataLoading,
} from "./pivotColumns";
import { ProgressSpinner } from "primereact/progressspinner";
import { PivotTable } from "./PivotTable";
import { CrossTabTable } from "./CrossTabTable";
import styles from "./PivotTable.module.css";
import { v4 as uuid } from "uuid";
import {
  String as RtString,
  Literal,
  Union,
  Null,
  Array as RtArray,
  Object as RtObject,
} from "runtypes";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type ViewMode = "hierarchical" | "crosstab";

type CardConfig = {
  id: string;
  primaryKey: string;
  secondaryKey: string | null;
  viewMode: ViewMode;
};

const CardConfigArrayRt = RtArray(
  RtObject({
    id: RtString,
    primaryKey: RtString,
    secondaryKey: Union(RtString, Null),
    viewMode: Union(Literal("hierarchical"), Literal("crosstab")),
  })
);

const VIEW_OPTIONS = [
  { label: "Grouped", value: "hierarchical" },
  { label: "Cross-tab", value: "crosstab" },
];

function makeDefaultCards(): CardConfig[] {
  return [
    {
      id: uuid(),
      primaryKey: "state",
      secondaryKey: "county",
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "year_built",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "construction",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "sic_code",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "flood_zone_fema",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "earthquake_zone",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "sprinkler",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "rms_dist_to_coast",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "katrisk_fema_flood_zone",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "katrisk_flood_score",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "nearmap_roof_age",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "nearmap_roof_material",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "nearmap_roof_shape",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "wind_gate",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
    {
      id: uuid(),
      primaryKey: "eq_gate",
      secondaryKey: null,
      viewMode: "hierarchical",
    },
  ];
}

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = "pivot-panel-";

function parseCards(raw: string | null): CardConfig[] {
  if (!raw) return makeDefaultCards();
  try {
    const stored = CardConfigArrayRt.check(JSON.parse(raw));
    if (stored.length > 0) return stored;
  } catch {
    // Corrupted or schema-mismatched data — fall through to defaults
  }
  return makeDefaultCards();
}

// ---------------------------------------------------------------------------
// Shared dropdown helpers
// ---------------------------------------------------------------------------

const fieldOptionTemplate = (option: GroupableField) => (
  <div>
    <span className={styles.fieldOptionGroup}>{option.group}</span>
    {" / "}
    {option.label}
  </div>
);

const selectedFieldTemplate = (
  option: GroupableField | null,
  props: DropdownProps
) => {
  if (!option) return <span>{props.placeholder}</span>;
  return <span>{option.label}</span>;
};

// ---------------------------------------------------------------------------
// PivotCard — one independently configurable pivot
// ---------------------------------------------------------------------------
// NOTE: Each PivotCard recomputes its pivot from the full address array
// independently. With 8+ default cards this means multiple passes over the
// data when `addresses` changes. This is fine for typical SOV sizes (up to
// a few thousand locations) but could be sluggish for very large SOVs.

type PivotCardProps = {
  addresses: RawAddressWithIndices[];
  context: SovGridContext;
  config: CardConfig;
  isNew?: boolean;
  onChange: (id: string, update: Partial<CardConfig>) => void;
  onRemove: (id: string) => void;
};

const PivotCard: React.FC<PivotCardProps> = ({
  addresses,
  context,
  config,
  isNew,
  onChange,
  onRemove,
}) => {
  const { id, primaryKey, secondaryKey, viewMode } = config;

  const primary = useMemo(
    () => GROUPABLE_FIELDS.find((f) => f.key === primaryKey),
    [primaryKey]
  );

  const secondary = useMemo(
    () =>
      secondaryKey
        ? GROUPABLE_FIELDS.find((f) => f.key === secondaryKey)
        : undefined,
    [secondaryKey]
  );

  const secondaryOptions = useMemo(
    () => GROUPABLE_FIELDS.filter((f) => f.key !== primaryKey),
    [primaryKey]
  );

  // If either selected field depends on data that's still streaming, defer
  // building the pivot — partial data produces misleading "N/A (not modeled)"
  // inflation that drains as chunks arrive.
  const isLoading =
    isFieldDataLoading(primary, context) ||
    isFieldDataLoading(secondary, context);

  const pivotResult: PivotTableResult | null = useMemo(() => {
    if (!primary || isLoading) return null;
    if (viewMode === "crosstab" && secondary) return null;
    return buildPivotTable(
      addresses,
      configFromFields(primary, secondary),
      context
    );
  }, [addresses, context, primary, secondary, viewMode, isLoading]);

  const crossTabResult: CrossTabResult | null = useMemo(() => {
    if (!primary || !secondary || viewMode !== "crosstab" || isLoading) {
      return null;
    }
    return buildCrossTab(addresses, primary, secondary, context);
  }, [addresses, context, primary, secondary, viewMode, isLoading]);

  return (
    <div
      className={`${styles.pivotCardConfigurable}${isNew ? ` ${styles.pivotCardNew}` : ""}`}
    >
      <div className={styles.pivotCardHeader}>
        <div className={styles.pivotControls}>
          <div className={styles.pivotControlGroup}>
            <label className={styles.pivotControlLabel}>Group By</label>
            <Dropdown
              value={primary}
              options={GROUPABLE_FIELDS}
              onChange={(e) =>
                onChange(id, {
                  primaryKey: (e.value as GroupableField).key,
                })
              }
              optionLabel="label"
              dataKey="key"
              itemTemplate={fieldOptionTemplate}
              valueTemplate={selectedFieldTemplate}
              placeholder="Select field..."
              filter
              className={styles.dropdownPrimary}
            />
          </div>
          <div className={styles.pivotControlGroup}>
            <label className={styles.pivotControlLabel}>Then By</label>
            <Dropdown
              value={secondary ?? null}
              options={secondaryOptions}
              onChange={(e) =>
                onChange(id, {
                  secondaryKey: e.value
                    ? (e.value as GroupableField).key
                    : null,
                })
              }
              optionLabel="label"
              dataKey="key"
              itemTemplate={fieldOptionTemplate}
              valueTemplate={selectedFieldTemplate}
              placeholder="(None)"
              showClear
              filter
              className={styles.dropdownSecondary}
            />
          </div>
          {secondary && (
            <div className={styles.pivotControlGroup}>
              <label className={styles.pivotControlLabel}>View</label>
              <SelectButton
                value={viewMode}
                onChange={(e) => {
                  if (e.value) onChange(id, { viewMode: e.value });
                }}
                options={VIEW_OPTIONS}
              />
            </div>
          )}
        </div>
        <Button
          icon="pi pi-times"
          text
          rounded
          severity="secondary"
          onClick={() => onRemove(id)}
          className={styles.pivotCardRemove}
          tooltip="Remove pivot"
          tooltipOptions={{ showDelay: 500 }}
        />
      </div>

      <div className={styles.pivotResultArea}>
        {isLoading ? (
          <div className={styles.pivotLoading}>
            <ProgressSpinner
              style={{ width: "32px", height: "32px" }}
              strokeWidth="4"
            />
            <span>Loading data…</span>
          </div>
        ) : viewMode === "crosstab" &&
          crossTabResult &&
          primary &&
          secondary ? (
          <CrossTabTable
            data={crossTabResult}
            rowLabel={primary.label}
            colLabel={secondary.label}
          />
        ) : pivotResult && primary ? (
          <PivotTable
            pivotKey={primary.key}
            pivotData={pivotResult}
            label={primary.label}
            compare={primary.compare}
          />
        ) : (
          <div>Select a field to group by.</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PivotPanel — manages a grid of PivotCards with localStorage persistence
// ---------------------------------------------------------------------------

type PivotPanelProps = {
  addresses: RawAddressWithIndices[];
  context: SovGridContext;
  reportId: string;
};

export const PivotPanel: React.FC<PivotPanelProps> = ({
  addresses,
  context,
  reportId,
}) => {
  let newCardId: string | null = null;

  const { value: rawCards, set: saveCards } = useStoredValue(
    `${STORAGE_KEY_PREFIX}${reportId}`
  );
  const cards = useMemo(() => parseCards(rawCards), [rawCards]);

  const setCards = (updater: (prev: CardConfig[]) => CardConfig[]) => {
    saveCards(JSON.stringify(updater(cards)));
  };

  const updateCard = (id: string, update: Partial<CardConfig>) => {
    setCards((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...update } : c))
    );
  };

  const removeCard = (id: string) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
  };

  const addCard = () => {
    const id = uuid();
    newCardId = id;
    setCards((prev) => [
      {
        id,
        primaryKey: "state",
        secondaryKey: null,
        viewMode: "hierarchical",
      },
      ...prev,
    ]);
  };

  const resetToDefaults = () => {
    confirmDialog({
      message: "Reset all pivots to defaults?",
      header: "Reset Pivots",
      icon: "pi pi-refresh",
      acceptClassName: "p-button-danger",
      accept: () => setCards(() => makeDefaultCards()),
    });
  };

  return (
    <div className={styles.pivotPanelContainer}>
      <div className={styles.pivotAddRow}>
        <Button
          label="Add Pivot"
          icon="pi pi-plus"
          text
          severity="secondary"
          onClick={addCard}
        />
        <Button
          label="Reset"
          icon="pi pi-refresh"
          text
          severity="secondary"
          onClick={resetToDefaults}
        />
      </div>
      <div className={styles.pivotCardGrid}>
        {cards.map((card) => {
          const isNew = card.id === newCardId;
          if (isNew) newCardId = null;
          return (
            <PivotCard
              key={card.id}
              addresses={addresses}
              context={context}
              config={card}
              isNew={isNew}
              onChange={updateCard}
              onRemove={removeCard}
            />
          );
        })}
      </div>
    </div>
  );
};
